import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRentals, RentalListing } from "@/hooks/use-rentals";
import { usePortfolio } from "@/hooks/use-portfolio";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, Sparkles } from "lucide-react";

const RENTAL_PHOTOS_BUCKET = "rental-photos";

/** Object path inside `rental-photos` parsed from a Supabase public URL. */
function rentalPhotoObjectPathFromPublicUrl(url: string): string | null {
  const marker = `/object/public/${RENTAL_PHOTOS_BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  const raw = url.slice(idx + marker.length).split(/[?#]/)[0];
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: RentalListing | null;
}

export function ListingPhotosDialog({ open, onOpenChange, listing }: Props) {
  const { user } = useAuth();
  const { updateListing } = useRentals();
  const { portfolio } = usePortfolio();
  const [uploading, setUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [removingUrl, setRemovingUrl] = useState<string | null>(null);
  const photos = listing?.photos || [];

  const uploadPhotos = useCallback(
    async (files: FileList) => {
      if (!listing || !user) return;
      setUploading(true);
      try {
        const newUrls: string[] = [];
        for (const file of Array.from(files)) {
          if (!file.type.startsWith("image/")) {
            toast.error(`${file.name} is not an image`);
            continue;
          }
          if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name} exceeds 10MB limit`);
            continue;
          }
          const ext = file.name.split(".").pop() || "jpg";
          const path = `${user.id}/${listing.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
          const { error } = await supabase.storage
            .from(RENTAL_PHOTOS_BUCKET)
            .upload(path, file);
          if (error) {
            toast.error(`Failed to upload ${file.name}`);
            continue;
          }
          const { data: urlData } = supabase.storage
            .from(RENTAL_PHOTOS_BUCKET)
            .getPublicUrl(path);
          newUrls.push(urlData.publicUrl);
        }
        if (newUrls.length > 0) {
          const allPhotos = [...photos, ...newUrls];
          updateListing.mutate({ id: listing.id, photos: allPhotos } as any);
          toast.success(
            `${newUrls.length} photo${newUrls.length > 1 ? "s" : ""} uploaded`,
          );
        }
      } catch (err) {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [listing, user, photos, updateListing],
  );

  const removePhoto = useCallback(
    async (url: string) => {
      if (!listing) return;
      setRemovingUrl(url);
      try {
        const path = rentalPhotoObjectPathFromPublicUrl(url);
        if (path) {
          const { error } = await supabase.storage
            .from(RENTAL_PHOTOS_BUCKET)
            .remove([path]);
          if (error) {
            toast.error(error.message || "Failed to delete photo from storage");
            return;
          }
        }
        const updated = photos.filter((p) => p !== url);
        await updateListing.mutateAsync({
          id: listing.id,
          photos: updated.length > 0 ? updated : null,
        });
      } catch {
        // updateListing mutation surfaces errors via its onError toast
      } finally {
        setRemovingUrl(null);
      }
    },
    [listing, photos, updateListing],
  );

  if (!listing) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Listing Photos</DialogTitle>
          <DialogDescription>
            Manage photos for "{listing.title}" — these are included in your
            syndication feed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload area */}
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/50 transition-colors">
            <input
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files && uploadPhotos(e.target.files)}
              disabled={uploading}
            />
            {uploading ? (
              <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
            ) : (
              <ImagePlus className="h-8 w-8 text-muted-foreground" />
            )}
            <p className="text-sm text-muted-foreground mt-2">
              {uploading
                ? "Uploading..."
                : "Click or drag photos here (max 10MB each)"}
            </p>
          </label>

          {/* Photo grid */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((url, i) => (
                <div
                  key={url}
                  className="relative group rounded-lg overflow-hidden border border-border aspect-[4/3]"
                >
                  <img
                    src={url}
                    alt={`Photo ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  <button
                    type="button"
                    disabled={!!removingUrl}
                    onClick={() => removePhoto(url)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-background/80 text-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                  >
                    {removingUrl === url ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                  {i === 0 && (
                    <span className="absolute bottom-2 left-2 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-medium">
                      Cover
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-4">
              No photos yet
            </p>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {photos.length} photo{photos.length !== 1 ? "s" : ""} • First
              photo is used as cover image in feeds
            </p>
            {photos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                disabled={regenerating}
                onClick={async () => {
                  if (!listing) return;
                  setRegenerating(true);
                  try {
                    const prop = portfolio.find(
                      (p) => p.id === listing.pipeline_property_id,
                    );
                    const { data, error } = await supabase.functions.invoke(
                      "generate-listing-description",
                      {
                        body: {
                          property: prop?.property || {},
                          listing: {
                            title: listing.title,
                            rent_amount: listing.rent_amount,
                            security_deposit: listing.security_deposit,
                            lease_term_months: listing.lease_term_months,
                            pet_policy: listing.pet_policy,
                          },
                          photoUrls: photos.slice(0, 4),
                        },
                      },
                    );
                    if (error) throw error;
                    if (data?.error) {
                      toast.error(data.error);
                      return;
                    }
                    if (data?.description) {
                      updateListing.mutate({
                        id: listing.id,
                        description: data.description,
                      } as any);
                      toast.success(
                        "Description regenerated with photo context!",
                      );
                    }
                  } catch {
                    toast.error("Failed to regenerate description");
                  } finally {
                    setRegenerating(false);
                  }
                }}
              >
                {regenerating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                )}
                {regenerating
                  ? "Generating..."
                  : "Regenerate Description with Photos"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
